import { useEffect } from "react";

export function pageScrollY() {
  const root = document.scrollingElement;
  return Math.max(
    window.scrollY || 0,
    root?.scrollTop || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0
  );
}

export function useHomeMotion(enabled, scope) {
  useEffect(() => {
    if (!enabled) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const listeners = [];
    const unbinds = [];
    const intervals = [];
    const timeouts = [];
    const rafs = [];

    const onScroll = (fn) => {
      const wrap = () => fn();
      const opts = { passive: true };
      window.addEventListener("scroll", wrap, opts);
      listeners.push(["scroll", wrap]);
      [document.documentElement, document.body, window.visualViewport].filter(Boolean).forEach((target) => {
        target.addEventListener("scroll", wrap, opts);
        unbinds.push(() => target.removeEventListener("scroll", wrap, opts));
      });
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", wrap, opts);
        unbinds.push(() => window.visualViewport.removeEventListener("resize", wrap, opts));
      }
      fn();
    };

    const desktopMq = window.matchMedia("(min-width: 1101px)");
    const heroEl = document.querySelector(".hero");
    const heroContent = document.getElementById("hero-content");
    if (heroEl && heroContent) {
      const baseHeroScale = () => {
        const w = window.innerWidth;
        if (w <= 1100) return 1;
        return Math.min(1, Math.max(0.6, (w - 96) / 911));
      };
      let last = "";
      onScroll(() => {
        if (!desktopMq.matches) {
          if (last !== "") {
            heroContent.style.removeProperty("transform");
            last = "";
          }
          return;
        }
        const progress = Math.min(Math.max(pageScrollY() / (heroEl.offsetHeight * 0.8), 0), 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const scale = baseHeroScale() * (1 - eased * 0.12);
        const t = `scale(${scale.toFixed(4)})`;
        if (t !== last) {
          heroContent.style.transform = t;
          last = t;
        }
      });
    }

    const container = document.querySelector(".intro-copy");
    const paras = container ? Array.from(container.querySelectorAll(".intro-type")) : [];
    if (container && paras.length) {
      const splitLines = (p) => {
        if (p.dataset.twText === undefined) p.dataset.twText = p.textContent;
        const tokens = p.dataset.twText.split(/(\s+)/);
        p.innerHTML = "";
        const spans = tokens.map((t) => {
          const s = document.createElement("span");
          s.textContent = t;
          p.appendChild(s);
          return s;
        });
        const lines = [];
        let cur = null;
        let curTop = null;
        spans.forEach((s) => {
          const top = s.offsetTop;
          if (curTop === null || top !== curTop) {
            cur = [];
            lines.push(cur);
            curTop = top;
          }
          cur.push(s.textContent);
        });
        p.innerHTML = "";
        return lines.map((words) => {
          const line = document.createElement("span");
          line.className = "tw-line";
          line.textContent = words.join("").replace(/^\s+|\s+$/g, "");
          p.appendChild(line);
          return line;
        });
      };

      let lines = paras.flatMap((p) => splitLines(p));
      let lastShown = null;
      const apply = () => {
        const vh = window.innerHeight;
        const top = container.getBoundingClientRect().top;
        const start = vh * 0.88;
        const end = vh * 0.32;
        let progress = (start - top) / (start - end);
        progress = progress < 0 ? 0 : progress > 1 ? 1 : progress;
        const shown = Math.floor(progress * (lines.length + 1));
        if (shown === lastShown) return;
        lastShown = shown;
        const n = lines.length;
        lines.forEach((line, i) => {
          line.classList.toggle("is-in", progress >= (i + 1) / (n + 1));
        });
      };
      if (reduce) lines.forEach((l) => l.classList.add("is-in"));
      else onScroll(apply);
    }

    const rows = document.querySelectorAll(".tiles-row");
    if (rows.length) {
      onScroll(() => {
        rows.forEach((row) => {
          const rect = row.getBoundingClientRect();
          const vh = window.innerHeight;
          const start = vh * 0.92;
          const end = vh * 0.42;
          let p = (start - rect.top) / (start - end);
          p = Math.min(1, Math.max(0, p));
          row.style.setProperty("--reveal", p.toFixed(4));
        });
      });
    }

    document.querySelectorAll(".reveal").forEach((el) => {
      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -12% 0px", threshold: 0.15 }
      );
      io.observe(el);
    });

    const path = document.getElementById("planeMotionPath");
    const sprite = document.querySelector(".plane-sprite");
    const mask = document.getElementById("planeTrailMaskPath");
    const svg = document.querySelector(".plane-trail");
    const fly = document.querySelector(".plane-fly");
    const featured = document.querySelector(".featured-work");
    const projects = document.querySelector(".projects");
    if (path && sprite && mask && svg && fly && featured && projects && path.getTotalLength) {
      const sizeFly = () => {
        const top = projects.offsetTop;
        const bottom = featured.offsetTop + featured.offsetHeight;
        fly.style.top = `${top - featured.offsetTop}px`;
        fly.style.height = `${bottom - top}px`;
      };
      sizeFly();
      window.addEventListener("resize", sizeFly, { passive: true });
      listeners.push(["resize", sizeFly]);

      const len = path.getTotalLength();
      mask.style.strokeDasharray = String(len);
      onScroll(() => {
        const span = fly.offsetHeight - window.innerHeight * 0.2;
        const start = fly.getBoundingClientRect().top + pageScrollY() - window.innerHeight * 0.15;
        let p = (pageScrollY() - start) / Math.max(span, 1);
        p = Math.min(1, Math.max(0, p));
        const pt = path.getPointAtLength(p * len);
        const pt2 = path.getPointAtLength(Math.min(len, p * len + 12));
        const box = svg.viewBox.baseVal;
        const rect = svg.getBoundingClientRect();
        const sx = rect.width / box.width;
        const sy = rect.height / box.height;
        const angle =
          (Math.atan2((pt2.y - pt.y) * sy, (pt2.x - pt.x) * sx) * 180) / Math.PI;
        sprite.style.opacity = p > 0.03 && p < 0.97 ? "1" : "0";
        sprite.style.transform = `translate(${pt.x * sx}px, ${pt.y * sy}px) translate(-50%, -50%) rotate(${angle}deg)`;
        mask.style.strokeDashoffset = String(len * (1 - p));
      });
    }

    const tCards = document.getElementById("t-cards");
    const testiEl = document.querySelector(".testimonials");
    if (tCards && testiEl) {
      const SCRUB = 460;
      const ease = (t) => {
        const clamped = Math.max(0, Math.min(1, t));
        return clamped * clamped * (3 - 2 * clamped);
      };
      let flowTop = null;
      const measureFlowTop = () => {
        const prevPos = testiEl.style.position;
        const prevTop = testiEl.style.top;
        testiEl.style.position = "static";
        testiEl.style.top = "auto";
        flowTop = testiEl.getBoundingClientRect().top + pageScrollY();
        testiEl.style.position = prevPos;
        testiEl.style.top = prevTop;
      };
      onScroll(() => {
        if (!desktopMq.matches) {
          tCards.style.removeProperty("--spread");
          return;
        }
        if (reduce) {
          tCards.style.setProperty("--spread", "1");
          return;
        }
        const rect = testiEl.getBoundingClientRect();
        if (!rect.height) return;
        if (rect.top > 0.5) flowTop = rect.top + pageScrollY();
        else if (flowTop == null) measureFlowTop();
        const past = pageScrollY() - (flowTop ?? 0);
        tCards.style.setProperty("--spread", ease(past / SCRUB).toFixed(4));
      });
      const invalidate = () => {
        flowTop = null;
      };
      window.addEventListener("resize", invalidate, { passive: true });
      listeners.push(["resize", invalidate]);
    }

    const featuredEl = document.querySelector(".featured-work");
    if (featuredEl) {
      const pin = () => {
        if (!desktopMq.matches) {
          featuredEl.style.removeProperty("top");
          return;
        }
        const top = Math.min(0, window.innerHeight - featuredEl.offsetHeight);
        featuredEl.style.top = `${top}px`;
      };
      pin();
      window.addEventListener("resize", pin, { passive: true });
      listeners.push(["resize", pin]);
    }

    const intro = document.querySelector(".about-intro");
    const scene = document.getElementById("ai-scene");
    const photos = document.querySelector(".ai-photos");
    const photosFrame = document.querySelector(".ai-photos-frame");
    const ffSection = document.querySelector(".about-fun");
    const ffStage = document.querySelector(".about-fun-stage");
    const ffCards = ffStage ? Array.from(ffStage.querySelectorAll(".ff-card")) : [];
    const fitCanvases = () => {
      if (intro) {
        const scale = Math.min(1, window.innerHeight / 1024, window.innerWidth / 1440);
        intro.style.setProperty("--intro-scale", String(scale));
        intro.style.setProperty("--intro-offset", `${Math.max(0, window.innerHeight - 1024 * scale) / 2}px`);
      }
      if (photosFrame || photos) {
        const scale = String(Math.min(1, (window.innerWidth - 48) / 516));
        (photosFrame || photos).style.setProperty("--pol-scale", scale);
      }
      if (ffStage && ffCards.length) {
        let minL = Infinity;
        let minT = Infinity;
        let maxR = -Infinity;
        let maxB = -Infinity;
        ffCards.forEach((card) => {
          const halfW = card.offsetWidth / 2;
          minL = Math.min(minL, card.offsetLeft - halfW);
          maxR = Math.max(maxR, card.offsetLeft + halfW);
          minT = Math.min(minT, card.offsetTop);
          maxB = Math.max(maxB, card.offsetTop + card.offsetHeight);
        });
        const cw = maxR - minL || 1440;
        const ch = maxB - minT || 1088;
        const fitted = Math.min(1, (window.innerWidth - 40) / cw, (window.innerHeight - 40) / ch);
        ffStage.style.setProperty("--ff-fit", Math.max(0.1, fitted).toFixed(4));
        const scx = ffStage.offsetWidth / 2;
        const scy = ffStage.offsetHeight / 2;
        const pile = [-8, 6, -4, 7, -6, 5];
        ffCards.forEach((card, i) => {
          const gcx = card.offsetLeft;
          const gcy = card.offsetTop + card.offsetHeight / 2;
          card.style.setProperty("--dx", `${(scx - gcx).toFixed(1)}px`);
          card.style.setProperty("--dy", `${(scy - gcy).toFixed(1)}px`);
          card.style.setProperty("--rot", `${pile[i % pile.length]}deg`);
        });
      }
    };
    fitCanvases();
    window.addEventListener("resize", fitCanvases, { passive: true });
    listeners.push(["resize", fitCanvases]);

    if (ffSection && ffStage && ffCards.length) {
      let flowTop = 0;
      let lastOpen = "";
      const smooth = (x) => {
        const t = Math.min(1, Math.max(0, x));
        return t * t * (3 - 2 * t);
      };
      onScroll(() => {
        if (window.matchMedia("(max-width: 1100px)").matches) {
          ffStage.style.setProperty("--ff-open", "1");
          return;
        }
        if (reduce) {
          ffStage.style.setProperty("--ff-open", "1");
          return;
        }
        const rect = ffSection.getBoundingClientRect();
        if (rect.top > 0.5) flowTop = pageScrollY() + rect.top;
        else if (!flowTop) flowTop = ffSection.offsetTop;
        const vh = window.innerHeight || 1;
        const start = flowTop - vh * 0.3;
        const end = flowTop + vh * 0.7;
        const open = smooth((pageScrollY() - start) / Math.max(end - start, 1));
        const next = open.toFixed(4);
        if (next !== lastOpen) {
          ffStage.style.setProperty("--ff-open", next);
          lastOpen = next;
        }
      });
    }

    if (intro && scene) {
      onScroll(() => {
        const rect = intro.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / Math.max(intro.offsetHeight * 0.5, 1)));
        const eased = 1 - Math.pow(1 - progress, 3);
        scene.style.setProperty("--intro-scroll", (1 - eased * 0.12).toFixed(4));
      });
    }

    const footer = document.querySelector(".site-footer");
    if (footer) {
      onScroll(() => {
        const rect = footer.getBoundingClientRect();
        const p = Math.min(
          1,
          Math.max(0, (window.innerHeight - rect.top) / Math.max(window.innerHeight * 0.85, 1))
        );
        footer.style.setProperty("--ft-in", p.toFixed(3));
      });
    }

    let cursorRaf = 0;
    const cursor = document.getElementById("dot-cursor");
    if (cursor) {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
      let rx = x;
      let ry = y;
      let visible = false;
      const reduceCursor = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const hotSel = "a, button, [role='button'], input, textarea, label, .ai-polaroid, .project-tile, .story-card, .ff-card, .gh-day";

      const move = (e) => {
        x = e.clientX;
        y = e.clientY;
        if (!visible) {
          visible = true;
          rx = x;
          ry = y;
          cursor.classList.add("is-on");
        }
        cursor.classList.toggle("is-hot", Boolean(e.target.closest(hotSel)));
      };
      const leave = () => {
        visible = false;
        cursor.classList.remove("is-on", "is-hot");
      };
      const tick = () => {
        if (reduceCursor) {
          rx = x;
          ry = y;
        } else {
          rx += (x - rx) * 0.18;
          ry += (y - ry) * 0.18;
        }
        cursor.style.setProperty("--x", `${x.toFixed(1)}px`);
        cursor.style.setProperty("--y", `${y.toFixed(1)}px`);
        cursor.style.setProperty("--rx", `${rx.toFixed(1)}px`);
        cursor.style.setProperty("--ry", `${ry.toFixed(1)}px`);
        cursorRaf = window.requestAnimationFrame(tick);
      };
      cursorRaf = window.requestAnimationFrame(tick);
      window.addEventListener("pointermove", move, { passive: true });
      window.addEventListener("blur", leave);
      listeners.push(["pointermove", move], ["blur", leave]);
    }

    return () => {
      listeners.forEach(([type, fn]) => window.removeEventListener(type, fn));
      unbinds.forEach((fn) => fn());
      timeouts.forEach((id) => window.clearTimeout(id));
      intervals.forEach((id) => window.clearInterval(id));
      rafs.forEach((id) => cancelAnimationFrame(id));
      cancelAnimationFrame(cursorRaf);
    };
  }, [enabled, scope]);
}
